import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { EmpresaApiService } from '../services/empresa-api.service';
import { CompanyRelationshipTypeApiService } from '../services/company-relationship-type-api.service';
import { CompanySizeApiService } from '../services/company-size-api.service';
import { CompanySectorApiService } from '../services/company-sector-api.service';
import { CompanyTaxTypeApiService } from '../services/company-tax-type-api.service';
import { CompanyStateLeadApiService } from '../services/company-state-lead-api.service';
import { CompanyCycleApiService } from '../services/company-cycle-api.service';
import {
  CompanyResponse,
  CompanySearchParams,
  CompanyRelationshipType,
  CompanySize,
  CompanySector,
  CompanyTaxType,
  CompanyStateLeadResponse,
  CompanyCycleResponse,
  Company,
  ContactCompanyResponse
} from '../models/empresa.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { EmpresaDetailModalComponent } from '../empresa-detail-modal/empresa-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ImportExportModalComponent } from '../../../shared/components/ui/import-export-modal/import-export-modal.component';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../shared/services/permission.service';
import { countryFromPhone, COUNTRIES_DATA } from '../../../shared/data/countries.data';
import { CountrySelectComponent } from '../../../shared/components/form/country-select/country-select.component';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-empresa-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    EmpresaDetailModalComponent,
    ImportExportModalComponent,
    HasPermissionDirective,
    CountrySelectComponent
  ],
  templateUrl: './empresa-list.component.html'
})
export class EmpresaListComponent implements OnInit {
  empresas: CompanyResponse[] = [];
  loading = false;
  error = '';

  /** Código ISO (minúscula) del país derivado del teléfono, para la bandera flag-icons. */
  countryFlagCode(phone?: string | null): string | null {
    return countryFromPhone(phone)?.code.toLowerCase() ?? null;
  }
  /** Nombre del país derivado del teléfono (tooltip). */
  countryName(phone?: string | null): string {
    return countryFromPhone(phone)?.name ?? '';
  }

  /** Filtro por país (por prefijo telefónico). */
  readonly filterCountries = COUNTRIES_DATA;
  filtroPais = '';
  onCountryFilterChange(code: string): void {
    this.filtroPais = code;
    this.buscar();
  }

  // Catálogos para filtros
  relationshipTypes: CompanyRelationshipType[] = [];
  companySizes: CompanySize[] = [];
  companySectors: CompanySector[] = [];
  taxTypes: CompanyTaxType[] = [];
  companyStateLeads: CompanyStateLeadResponse[] = [];
  companyCycles: CompanyCycleResponse[] = [];

  // Modal de detalle
  showDetailModal = false;
  selectedCompany: Company | null = null;

  // Import/Export modal
  showImportExportModal = false;

  // Filtro de responsable
  private readonly OWNER_FILTER_KEY = 'crm_owner_filter_empresas';
  filtroOwner: 'all' | 'mine' = localStorage.getItem(this.OWNER_FILTER_KEY) === 'mine' ? 'mine' : 'all';

  // Filtros
  showFilters = false;
  filtroNombre = '';
  filtroContacto = '';
  filtroRelationshipType: number | undefined;
  filtroSize: number | undefined;
  filtroSector: number | undefined;
  filtroStateLead: number | undefined;
  filtroCycle: number | undefined;

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'empresa-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 10;

  constructor(
    private empresaApi: EmpresaApiService,
    private relationshipTypeApi: CompanyRelationshipTypeApiService,
    private sizeApi: CompanySizeApiService,
    private sectorApi: CompanySectorApiService,
    private taxTypeApi: CompanyTaxTypeApiService,
    private stateLeadApi: CompanyStateLeadApiService,
    private cycleApi: CompanyCycleApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private permissions: PermissionService
  ) { }

  ngOnInit(): void {
    this.cargarCatalogos();
    this.cargarEmpresas();
  }

  /**
   * Permiso granular del módulo 'companies' (Super Admin = true vía PermissionService).
   * Reemplaza el antiguo check por roleName.
   */
  get puedeImportar(): boolean {
    return this.permissions.canImport('companies');
  }

  get puedeExportar(): boolean {
    return this.permissions.canExport('companies');
  }

  /** El botón/modal se muestra si el usuario puede al menos una de las dos acciones. */
  get canImportExport(): boolean {
    return this.puedeImportar || this.puedeExportar;
  }

  abrirImportExport(): void {
    this.showImportExportModal = true;
  }

  cerrarImportExport(): void {
    this.showImportExportModal = false;
    this.cargarEmpresas();
  }

  private cargarCatalogos(): void {
    // Cargar catálogos para los filtros y modal
    this.relationshipTypeApi.getAll().subscribe({
      next: (res) => this.relationshipTypes = res.content,
      error: () => { }
    });

    this.sizeApi.getAll().subscribe({
      next: (res) => this.companySizes = res.content,
      error: () => { }
    });

    this.sectorApi.getAll().subscribe({
      next: (data) => this.companySectors = data,
      error: () => { }
    });

    this.taxTypeApi.getAll().subscribe({
      next: (res) => this.taxTypes = res.content,
      error: () => { }
    });

    this.stateLeadApi.getAll().subscribe({
      next: (res) => this.companyStateLeads = res.content,
      error: () => { }
    });

    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.cycleApi.getAll(businessId).subscribe({
      next: (res) => this.companyCycles = res.content,
      error: () => { }
    });
  }

  cargarEmpresas(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const params: CompanySearchParams = {
      businessId,
      searchTerm: this.filtroNombre || undefined,
      contactSearchTerm: this.filtroContacto || undefined,
      idCompanyRelationshipType: this.filtroRelationshipType,
      idCompanySize: this.filtroSize,
      idCompanySector: this.filtroSector,
      idCompanyStateLead: this.filtroStateLead,
      idCompanyCycle: this.filtroCycle,
      phonePrefix: this.filtroPais
        ? COUNTRIES_DATA.find(c => c.code === this.filtroPais)?.dialCode || undefined
        : undefined,
      userPropietyId: this.filtroOwner === 'mine' ? user?.id : undefined,
      page: this.globalPage,
      size: this.pageSize
    };

    this.empresaApi.searchCompanies(params).subscribe({
      next: (response) => {
        this.empresas = response.content;
        this.globalPage = response.number ?? this.globalPage;
        this.globalTotalPages = response.totalPages;
        this.globalTotalElements = response.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar empresas';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarEmpresas();
  }

  setOwnerFilter(value: 'all' | 'mine'): void {
    this.filtroOwner = value;
    localStorage.setItem(this.OWNER_FILTER_KEY, value);
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarEmpresas();
  }

  limpiarFiltro(): void {
    this.filtroOwner = 'all';
    this.filtroNombre = '';
    this.filtroContacto = '';
    this.filtroRelationshipType = undefined;
    this.filtroSize = undefined;
    this.filtroSector = undefined;
    this.filtroStateLead = undefined;
    this.filtroCycle = undefined;
    this.filtroPais = '';
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.cargarEmpresas();
  }

  nuevaEmpresa(): void {
    this.router.navigate(['/empresas/crear']);
  }

  vistaTablero(): void {
    this.router.navigate(['/empresas']);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  viewDetail(company: CompanyResponse): void {
    this.selectedCompany = company;
    this.showDetailModal = true;
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedCompany = null;
  }

  editarEmpresa(empresa: CompanyResponse): void {
    this.router.navigate(['/empresas/editar', empresa.id]);
  }

  async eliminarEmpresa(empresa: CompanyResponse): Promise<void> {
    if (!empresa.id) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar empresa',
      message: `¿Está seguro de eliminar la empresa "${empresa.commercialName}"?`
    });
    if (!ok) return;
    this.empresaApi.deleteCompany(empresa.id).subscribe({
      next: () => {
        this.cargarEmpresas();
        this.toast.deleted('Empresa');
      },
      error: (err) => {
        this.manejarErrorEliminar(err);
      }
    });
  }

  /** Inc 8: 409 COMPANY_HAS_ASSOCIATIONS → diálogo informativo con los conteos; otros errores → toast. */
  private manejarErrorEliminar(err: { status?: number; error?: { message?: string; details?: Record<string, unknown> } }): void {
    const details = err?.error?.details;
    if (err?.status === 409 && details?.['error'] === 'COMPANY_HAS_ASSOCIATIONS') {
      const partes: string[] = [];
      const agregar = (count: unknown, singular: string, plural: string) => {
        const n = typeof count === 'number' ? count : 0;
        if (n > 0) partes.push(`${n} ${n === 1 ? singular : plural}`);
      };
      agregar(details['contacts'], 'contacto', 'contactos');
      agregar(details['deals'], 'negociación', 'negociaciones');
      agregar(details['tasks'], 'tarea', 'tareas');
      agregar(details['tickets'], 'ticket', 'tickets');

      void this.confirmDialog.alert({
        title: 'No se puede eliminar la empresa',
        message: `No se puede eliminar la empresa porque tiene: ${partes.join(', ')}. Desvincula estos registros primero.`,
        variant: 'warning',
        confirmText: 'Entendido'
      });
      return;
    }
    this.toast.error({ title: 'Error', message: err?.error?.message || 'Error al eliminar la empresa' });
  }

  // Métodos auxiliares para badges.
  // inc38 quitó las columnas Tipo de Relación, Tamaño y Sector de la tabla, así que
  // getRelationshipTypeName / getSizeName / getSectorName se fueron con ellas. Los
  // catálogos siguen cargándose porque alimentan los filtros de la cabecera.
  getStateLeadName(id?: number): string {
    if (!id) return '—';
    const stateLead = this.companyStateLeads.find(s => s.id === id);
    return stateLead?.name || '—';
  }

  getCycleName(id?: number): string {
    if (!id) return '—';
    return this.companyCycles.find(c => c.id === id)?.name || '—';
  }

  // Paginación
  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.cargarEmpresas();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.buscar();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }

  get hasPrevPage(): boolean { return this.globalPage > 0; }
  get hasNextPage(): boolean { return this.globalPage < this.globalTotalPages - 1; }
  get pageDisplay(): number  { return this.globalPage + 1; }
}
